import React, { useState, useRef, useEffect, useCallback } from 'react';
import { initializeApp } from "firebase/app";
import { getDatabase, ref, push, onValue, off, set, serverTimestamp } from "firebase/database";
import { getAuth, signInAnonymously } from "firebase/auth";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faComment,
    faTimes,
    faVolumeUp,
    faVolumeMute,
    faPaperPlane,
    faUser,
    faEllipsisH
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import './LiveChat.css';

// Firebase configuration
const firebaseConfig = {
    apiKey: "AIzaSyAG6nUCtjJD6AArEOVQuKhWOn_92ZN61fI",
    authDomain: "my-live-chat-3fc2b.firebaseapp.com",
    databaseURL: "https://my-live-chat-3fc2b-default-rtdb.firebaseio.com",
    projectId: "my-live-chat-3fc2b",
    storageBucket: "my-live-chat-3fc2b.appspot.com",
    messagingSenderId: "791967909652",
    appId: "1:791967909652:web:645ce437412d99b9c5e401",
    measurementId: "G-6HTRMFRJJC"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getDatabase(app);
const auth = getAuth(app);

const LiveChat = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [showOptions, setShowOptions] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [isSupportTyping, setIsSupportTyping] = useState(false);
    const [soundEnabled, setSoundEnabled] = useState(true);
    const [chatId, setChatId] = useState(null);
    const [userId, setUserId] = useState(null);
    const [supportHasJoined, setSupportHasJoined] = useState(false);
    const [hasInitialized, setHasInitialized] = useState(false);
    const [showPulseAnimation, setShowPulseAnimation] = useState(false);
    const messagesEndRef = useRef(null);
    const inputTimeoutRef = useRef(null);

    // Sound effects
    const playSound = useCallback((frequency, type = 'sine', duration = 0.2) => {
        if (!soundEnabled) return;

        try {
            const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = audioCtx.createOscillator();
            const gainNode = audioCtx.createGain();

            oscillator.type = type;
            oscillator.connect(gainNode);
            gainNode.connect(audioCtx.destination);

            oscillator.frequency.value = frequency;
            gainNode.gain.setValueAtTime(0, audioCtx.currentTime);
            gainNode.gain.linearRampToValueAtTime(0.1, audioCtx.currentTime + 0.01);
            gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + duration);

            oscillator.start();
            oscillator.stop(audioCtx.currentTime + duration);
        } catch (e) {
            console.log("Audio not supported");
        }
    }, [soundEnabled]);

    // Play welcome sound and show animation
    const playWelcomeSound = useCallback(() => {
        if (!soundEnabled) return;

        // Play a pleasant welcome chime
        setTimeout(() => playSound(659, 'sine', 0.3), 0);    // E5
        setTimeout(() => playSound(784, 'sine', 0.3), 200);  // G5
        setTimeout(() => playSound(1047, 'sine', 0.5), 400); // C6
    }, [soundEnabled, playSound]);

    // Auto-initialize chat when component mounts
    useEffect(() => {
        if (!hasInitialized) {
            const initializeChat = async () => {
                try {
                    // Sign in anonymously
                    const userCredential = await signInAnonymously(auth);
                    setUserId(userCredential.user.uid);

                    // Create new chat
                    const newChatRef = push(ref(db, 'chats'));
                    setChatId(newChatRef.key);

                    // Set chat metadata first
                    await set(ref(db, `chats/${newChatRef.key}/metadata`), {
                        createdAt: serverTimestamp(),
                        status: "open",
                        userId: userCredential.user.uid,
                        lastActivity: serverTimestamp(),
                        typing: { user: false, support: false },
                        supportJoined: false,
                        visitorJoined: serverTimestamp()
                    });

                    // Add automatic welcome message
                    await push(ref(db, `chats/${newChatRef.key}/messages`), {
                        text: "Welcome to our website! 👋 We're here to help you with any questions you might have. How can we assist you today?",
                        sender: "bot",
                        timestamp: serverTimestamp(),
                        userId: userCredential.user.uid,
                        isWelcomeMessage: true
                    });

                    // Play welcome sound and show animation
                    playWelcomeSound();
                    setShowPulseAnimation(true);

                    // Stop animation after 3 seconds
                    setTimeout(() => {
                        setShowPulseAnimation(false);
                    }, 3000);

                    setHasInitialized(true);
                } catch (error) {
                    console.error("Error initializing chat:", error);
                }
            };

            // Initialize chat after a short delay to ensure component is mounted
            setTimeout(initializeChat, 1000);
        }
    }, [hasInitialized, playWelcomeSound]);

    // Listen for messages and typing status
    useEffect(() => {
        if (!chatId) return;

        const messagesRef = ref(db, `chats/${chatId}/messages`);
        const metadataRef = ref(db, `chats/${chatId}/metadata`);

        const handleMessagesChange = onValue(messagesRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const messagesArray = Object.entries(data).map(([id, message]) => ({
                    id,
                    ...message
                })).sort((a, b) => a.timestamp - b.timestamp);

                setMessages((prevMessages) => {
                    // Check if support agent has sent any messages
                    const supportMessages = messagesArray.filter(msg => msg.sender === 'support');
                    if (supportMessages.length > 0 && !supportHasJoined) {
                        setSupportHasJoined(true);
                        set(ref(db, `chats/${chatId}/metadata/supportJoined`), true);
                    }

                    // Play sound for new messages not sent by user (except initial welcome)
                    if (messagesArray.length > prevMessages.length) {
                        const newMessage = messagesArray[messagesArray.length - 1];
                        if (newMessage.sender !== 'user' && !newMessage.isWelcomeMessage) {
                            playSound(600);
                            setShowPulseAnimation(true);
                            setTimeout(() => setShowPulseAnimation(false), 2000);
                        }
                    }

                    return messagesArray;
                });
            }
        });

        const handleMetadataChange = onValue(metadataRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                if (data.typing) {
                    setIsSupportTyping(data.typing.support || false);
                }
                if (data.supportJoined !== undefined) {
                    setSupportHasJoined(data.supportJoined);
                }
            }
        });

        return () => {
            off(messagesRef, 'value', handleMessagesChange);
            off(metadataRef, 'value', handleMetadataChange);
        };
    }, [chatId, supportHasJoined, playSound]);

    // Scroll to bottom when messages change
    useEffect(() => {
        scrollToBottom();
    }, [messages, isSupportTyping]);

    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (inputMessage.trim() === '') return;

        try {
            playSound(400, 'square', 0.1);

            // Add user message
            await push(ref(db, `chats/${chatId}/messages`), {
                text: inputMessage,
                sender: "user",
                timestamp: serverTimestamp(),
                userId
            });

            // Update last activity and set user typing to false
            await set(ref(db, `chats/${chatId}/metadata/lastActivity`), serverTimestamp());
            await set(ref(db, `chats/${chatId}/metadata/typing/user`), false);

            setInputMessage('');

            // Only show bot responses if support hasn't joined yet
            if (!supportHasJoined) {
                setIsTyping(true);

                // Simulate bot response
                setTimeout(async () => {
                    const responses = [
                        "Thank you for your message! Our support team will be with you shortly.",
                        "I've received your message. A team member will respond to you soon!",
                        "Thanks for reaching out! We'll get back to you as quickly as possible.",
                        "Message received! Our team is reviewing your request and will respond shortly."
                    ];

                    const randomResponse = responses[Math.floor(Math.random() * responses.length)];

                    await push(ref(db, `chats/${chatId}/messages`), {
                        text: randomResponse,
                        sender: "bot",
                        timestamp: serverTimestamp()
                    });
                    setIsTyping(false);
                }, 1500);
            }
        } catch (error) {
            console.error("Error sending message:", error);
        }
    };

    const handleInputChange = async (e) => {
        setInputMessage(e.target.value);

        if (!chatId) return;

        if (inputTimeoutRef.current) {
            clearTimeout(inputTimeoutRef.current);
        } else {
            // Set user typing to true
            await set(ref(db, `chats/${chatId}/metadata/typing/user`), true);
        }

        inputTimeoutRef.current = setTimeout(async () => {
            // Set user typing to false after 3 seconds of inactivity
            await set(ref(db, `chats/${chatId}/metadata/typing/user`), false);
            inputTimeoutRef.current = null;
        }, 3000);
    };

    const toggleChat = () => {
        setIsOpen(!isOpen);
        setShowOptions(false);
    };

    const toggleOptions = () => {
        setShowOptions(!showOptions);
        setIsOpen(false);
    };

    const openWhatsApp = () => {
        window.open("https://wa.me/1234567890?text=Hello! I need assistance.", "_blank");
        setShowOptions(false);
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const getUnreadCount = () => {
        return messages.filter(m => m.sender !== 'user').length;
    };

    return (
        <div className="lc-container">
            {/* Main Chat Button */}
            <button
                className={`lc-main-button ${showPulseAnimation ? 'pulse-animation' : ''}`}
                onClick={toggleOptions}
                aria-label="Open chat options"
            >
                {showOptions || isOpen ? (
                    <FontAwesomeIcon icon={faTimes} />
                ) : (
                    <FontAwesomeIcon icon={faComment} />
                )}
                {!showOptions && !isOpen && getUnreadCount() > 0 && (
                    <div className="lc-notification-badge">
                        {getUnreadCount()}
                    </div>
                )}
            </button>

            {/* Options Menu */}
            <div className={`lc-options-menu ${showOptions ? 'show' : ''}`}>
                <div
                    className="lc-option-item"
                    onClick={toggleChat}
                >
                    <div className="lc-option-icon chat">
                        <FontAwesomeIcon icon={faComment} />
                    </div>
                    <div className="lc-option-text">
                        <div className="title">Live Chat</div>
                        <div className="subtitle">Chat with our team</div>
                    </div>
                </div>

                <div
                    className="lc-option-item whatsapp"
                    onClick={openWhatsApp}
                >
                    <div className="lc-option-icon whatsapp">
                        <FontAwesomeIcon icon={faWhatsapp} />
                    </div>
                    <div className="lc-option-text">
                        <div className="title">WhatsApp</div>
                        <div className="subtitle">Message us directly</div>
                    </div>
                </div>
            </div>

            {/* Chat Window */}
            <div className={`lc-window ${isOpen ? 'open' : ''}`}>
                {/* Header */}
                <div className="lc-header">
                    <div className="lc-header-content">
                        <div className="lc-header-avatar">
                            <FontAwesomeIcon icon={faUser} />
                        </div>
                        <div className="lc-header-info">
                            <div className="name">
                                {supportHasJoined ? "Support Agent" : "Support Team"}
                            </div>
                            <div className="status">
                                {isSupportTyping ? "Typing..." : "Online"}
                            </div>
                        </div>
                    </div>
                    <div className="lc-header-controls">
                        <button
                            className={`lc-header-button ${soundEnabled ? 'sound-active' : ''}`}
                            onClick={() => setSoundEnabled(!soundEnabled)}
                            title={soundEnabled ? "Mute sounds" : "Unmute sounds"}
                            aria-label={soundEnabled ? "Mute sounds" : "Unmute sounds"}
                        >
                            <FontAwesomeIcon icon={soundEnabled ? faVolumeUp : faVolumeMute} />
                        </button>
                        <button
                            className="lc-header-button"
                            onClick={toggleChat}
                            aria-label="Close chat"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                </div>

                {/* Messages */}
                <div className="lc-messages">
                    {messages.map((message) => (
                        <div
                            key={message.id}
                            className={`lc-message ${message.sender}`}
                        >
                            <div className={`lc-message-content ${message.sender} ${message.isWelcomeMessage ? 'welcome' : ''}`}>
                                <p className="lc-message-text">{message.text}</p>
                                <span className="lc-message-time">
                                    {formatTime(message.timestamp)}
                                </span>
                            </div>
                        </div>
                    ))}
                    {/* Only show bot typing if support hasn't joined yet */}
                    {isTyping && !supportHasJoined && (
                        <div className="lc-message bot">
                            <div className="lc-message-content bot">
                                <div className="lc-typing-indicator">
                                    <span className="lc-typing-dot"></span>
                                    <span className="lc-typing-dot"></span>
                                    <span className="lc-typing-dot"></span>
                                </div>
                            </div>
                        </div>
                    )}
                    {/* Show support typing indicator */}
                    {isSupportTyping && (
                        <div className="lc-message support">
                            <div className="lc-message-content support">
                                <div className="lc-typing-indicator">
                                    <span className="lc-typing-dot"></span>
                                    <span className="lc-typing-dot"></span>
                                    <span className="lc-typing-dot"></span>
                                </div>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <form onSubmit={handleSendMessage} className="lc-input-form">
                    <div className="lc-input-container">
                        <input
                            type="text"
                            value={inputMessage}
                            onChange={handleInputChange}
                            placeholder="Type your message..."
                            className="lc-input"
                            aria-label="Type your message"
                        />
                        <button
                            type="submit"
                            className="lc-send-btn"
                            disabled={!inputMessage.trim()}
                            aria-label="Send message"
                        >
                            <FontAwesomeIcon icon={faPaperPlane} />
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default LiveChat;